import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { QueryWithExecutionRecord } from '../../../../API';
import { QueryRecord, WorkbenchType } from '../../models/market-fan-exploration.model';
import { FanExplorationService } from '../../services/fan-exploration.service';
import { MARKET_FAN_EXPLORATION } from '../../../shared/utils/app.constants';
import { UserManagementService } from '../../../shared/services/user-management.service';

@Component({
  selector: 'app-market-fan-exploration',
  templateUrl: './market-fan-exploration.component.html',
  styleUrl: './market-fan-exploration.component.scss',
})
export class MarketFanExplorationComponent implements OnInit, OnDestroy {
  readonly WorkbenchType = WorkbenchType;
  readonly workbenchTabIndex = 0;
  readonly segmentTabIndex = 2;

  componentTitle: string = MARKET_FAN_EXPLORATION;
  selectedTabIndex = 0;
  selectedWorkbenchType: WorkbenchType = WorkbenchType.STANDARD_TEMPLATES;
  queryRecord: QueryRecord | null = null;
  segmentEditRecord: QueryWithExecutionRecord | null = null;
  standardEditRecord: QueryWithExecutionRecord | null = null;
  private routeParamSubscription?: Subscription;
  private skipUrlUpdate = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fanExplorationService: FanExplorationService,
    private userManagementService: UserManagementService,
  ) {}

  ngOnInit(): void {
    this.routeParamSubscription = this.route.paramMap.subscribe((params) => {
      void this.syncEditContextFromRoute(params.get('queryType'), params.get('queryId'), params.get('executionid'));
    });

    // Apply tab from query params if present
    this.route.queryParams.subscribe((queryParams) => {
      const tabParam = queryParams['tab'];
      if (tabParam !== undefined) {
        this.skipUrlUpdate = true;
        this.selectedTabIndex = parseInt(tabParam, 10);
        this.skipUrlUpdate = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.routeParamSubscription?.unsubscribe();
  }

  onWorkbenchTypeChange(type: WorkbenchType): void {
    if (type === WorkbenchType.STANDARD_TEMPLATES) {
      this.queryRecord = null;
    }
    this.updateUrlWithTabParam();
  }

  moveToCustomQuery(queryRecord: QueryRecord): void {
    this.queryRecord = queryRecord;
    this.selectedWorkbenchType = WorkbenchType.CUSTOM_QUERY;
    this.updateUrlWithTabParam(false);
  }

  moveToHistoryTab(): void {
    this.selectedTabIndex = this.historyTabIndex;
    this.updateUrlWithTabParam();
  }

  private applyEditRouteContext(queryType: string | null): void {
    if (!queryType) {
      this.selectedTabIndex = this.workbenchTabIndex;
      this.selectedWorkbenchType = WorkbenchType.STANDARD_TEMPLATES;
      return;
    }

    const normalizedQueryType = queryType.trim().toUpperCase();

    if (normalizedQueryType === 'CUSTOM') {
      this.selectedTabIndex = this.workbenchTabIndex;
      this.selectedWorkbenchType = WorkbenchType.CUSTOM_QUERY;
      return;
    }

    if (normalizedQueryType === 'STANDARD') {
      this.selectedTabIndex = this.workbenchTabIndex;
      this.selectedWorkbenchType = WorkbenchType.STANDARD_TEMPLATES;
      return;
    }

    if (normalizedQueryType === 'SEGMENT' || normalizedQueryType === 'SEGEMENT') {
      this.selectedTabIndex = this.segmentTabIndex;
      return;
    }

    this.selectedTabIndex = this.workbenchTabIndex;
    this.selectedWorkbenchType = WorkbenchType.STANDARD_TEMPLATES;
  }

  private async syncEditContextFromRoute(
    queryType: string | null,
    queryId: string | null,
    executionId: string | null,
  ): Promise<void> {
    try {
      let resolvedQueryType = queryType;

      if (queryId && executionId) {
        const editRecord = await this.fanExplorationService.getOrFetchEditQueryDetails(queryId, executionId);

        if (editRecord) {
          const recordQueryType = this.fanExplorationService.getEditQueryType(editRecord);
          resolvedQueryType = recordQueryType;
          this.applyPrefillDetails(editRecord, recordQueryType);
        }
      } else {
        this.fanExplorationService.clearEditQueryDetails();
        this.segmentEditRecord = null;
      }

      this.skipUrlUpdate = true;
      this.applyEditRouteContext(resolvedQueryType);
      // TODO to be checked later for different approach
      // Defer the reset so it stays true through Angular's change detection cycle
      setTimeout(() => {
        this.skipUrlUpdate = false;
      }, 0);
    } catch (error) {
      this.skipUrlUpdate = false;
      console.error('Failed to sync edit context from route', error);
    }
  }

  private applyPrefillDetails(editRecord: QueryWithExecutionRecord, queryType: string): void {
    if (queryType === 'CUSTOM') {
      const validationDetails = editRecord as QueryWithExecutionRecord & {
        validationStatus?: string | null;
        validationLogs?: string | null;
      };

      this.queryRecord = {
        name: editRecord.queryName?.trim() || '',
        description: editRecord.queryDescription?.trim() || '',
        query: editRecord.query?.trim() || '',
        queryId: editRecord.queryId?.trim() || '',
        executionId: editRecord.executionId?.trim() || '',
        validationStatus: (validationDetails.validationStatus as any) ?? null,
        validationLogs: validationDetails.validationLogs ?? null,
      };
      this.segmentEditRecord = null;
      return;
    }

    if (queryType === 'SEGMENT') {
      this.segmentEditRecord = editRecord;
      this.queryRecord = null;
      this.standardEditRecord = null;
      return;
    }

    if (queryType === 'STANDARD') {
      this.standardEditRecord = editRecord;
      this.segmentEditRecord = null;
      this.queryRecord = null;
      return;
    }

    this.queryRecord = null;
    this.segmentEditRecord = null;
    this.standardEditRecord = null;
  }

  updateUrlWithTabParam(clearEditContext = true): void {
    if (this.skipUrlUpdate) {
      return;
    }

    if (clearEditContext) {
      this.segmentEditRecord = null;
      this.queryRecord = null;
      this.standardEditRecord = null;
      this.fanExplorationService.clearEditQueryDetails();
    }

    const queryParams = this.selectedTabIndex > 0 ? { tab: this.selectedTabIndex } : {};
    this.router.navigate(['/market-fan-exploration'], {
      queryParams,
      preserveFragment: true,
    });
  }

  get isMarketingUser(): boolean {
    return this.userManagementService.isMarketingUser();
  }

  get historyTabIndex(): number {
    return this.isMarketingUser ? 2 : 3;
  }
}
